import { PageHero } from "@/components/PageHero";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";
import { PrimaryCTA, OutlineCTA } from "@/components/CTA";
import { Tick, Star } from "@/components/Annotation";
import { Tape, StickyNote } from "@/components/Notebook";
import { MethodologyTeaser } from "@/components/MethodologyTeaser";
import { ShareCaseCard } from "@/components/ShareCaseCard";
import { PageMeta } from "@/components/PageMeta";
import { ROUTES } from "@/config/links";
import { useLang } from "@/i18n";

const LOGOS = [
  { src: "/logos/amazon.png",  alt: "Amazon",                        w: "w-[150px] md:w-[190px]" },
  { src: "/logos/ert.png",     alt: "ΕΡΤ · Hellenic Broadcasting",   w: "w-[70px]  md:w-[90px]"  },
  { src: "/logos/avocado.png", alt: "Avocado Learning Experiences",  w: "w-[170px] md:w-[210px]" },
];

// Kept English across all locales per brand rules
const FEEDBACK_PHRASES = [
  "Interaction and material discussed",
  "Great facilitator — very good work",
  "The facilitator was very engaging",
  "Interactive activities. Passion. Respect. Politeness.",
  "Great presentation and very helpful",
  "Engaging, to the point and resourceful under different circumstances",
  "Tips for grounding · the energy of the presenter · the actual examples",
  "Interactive — with real examples and scenarios",
  "The role play with the actor and the body exercises",
  "Interactive for most of its duration — a quality often missing from workshops",
  "The interactive exercises and the incorporation of physical practice",
];

const CASE_STATS = [
  { value: "17",    label: "Participants" },
  { value: "100%",  label: "Feedback participation" },
  { value: "5.0/5", label: "Facilitator rating" },
  { value: "4.8/5", label: "Workshop rating" },
];

const PARTICIPANT_QUOTES = [
  { q: "The facilitator was very engaging",                                                                        role: "Participant · Recruitment team" },
  { q: "Great presentation and very helpful",                                                                      role: "Participant · Recruitment team" },
  { q: "The practical part through the cases",                                                                     role: "Participant · Recruitment team" },
  { q: "Engaging, to the point and resourceful under different circumstances",                                     role: "Participant · Recruitment team" },
  { q: "Interactive activities. Passion. Respect. Politeness.",                                                     role: "Participant · Recruitment team" },
  { q: "The role play with the actor and the body exercises",                                                       role: "Participant · Recruitment team" },
  { q: "The workshop was interactive for most of its duration — a quality that is often missing from many workshops", role: "Participant · Recruitment team" },
  { q: "Tips for grounding · the energy of the presenter · the actual examples",                                    role: "Participant · Recruitment team" },
];

const CusTrust = ({ t }) => (
  <section className="relative z-10 py-14 md:py-20 border-y border-ink/10 bg-paper-deep/40" data-testid="cus-trust">
    <Reveal className="max-w-[1400px] mx-auto px-5 md:px-10 mb-8 md:mb-10">
      <p className="font-body text-xs uppercase tracking-[0.24em] text-ink-soft text-center">
        {t("cus.trust_label")}
      </p>
    </Reveal>
    <div className="max-w-[1400px] mx-auto px-5 md:px-10 flex flex-wrap items-center justify-center gap-12 md:gap-20 mb-10 md:mb-14">
      {LOGOS.map((l) => (
        <img key={l.alt} src={l.src} alt={l.alt} draggable={false}
          className={`${l.w} h-auto opacity-90 hover:opacity-100 transition-opacity duration-500 select-none`} />
      ))}
    </div>
    <Reveal className="max-w-[1400px] mx-auto px-5 md:px-10 mb-5 md:mb-6">
      <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta text-center">
        {t("cus.trust_marquee_label")}
      </p>
    </Reveal>
    <div className="relative overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_6%,black_94%,transparent)]">
      <div className="flex w-max animate-marquee items-center gap-10 md:gap-14 whitespace-nowrap">
        {[...FEEDBACK_PHRASES, ...FEEDBACK_PHRASES].map((phrase, i) => (
          <span key={i} className="inline-flex items-center gap-10 md:gap-14">
            <span className="font-serif italic text-lg md:text-2xl text-ink/55 hover:text-ink transition-colors duration-500">
              &ldquo;{phrase}&rdquo;
            </span>
            <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-full bg-terracotta/60" />
          </span>
        ))}
      </div>
    </div>
  </section>
);

const RollingQuoteCards = () => (
  <div className="relative overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_5%,black_95%,transparent)]" data-testid="cus-rolling-quotes">
    <div className="flex w-max animate-marquee items-stretch gap-6 md:gap-8">
      {[...PARTICIPANT_QUOTES, ...PARTICIPANT_QUOTES].map((c, i) => (
        <figure key={i} className="relative bg-paper border border-ink/12 p-7 md:p-8 shadow-[4px_8px_24px_rgba(27,27,24,0.06)] w-[320px] md:w-[380px] shrink-0">
          <span aria-hidden="true" className="absolute -top-4 -left-1 font-serif text-6xl text-terracotta/50 leading-none">&ldquo;</span>
          <blockquote className="font-serif italic text-lg md:text-xl text-ink leading-snug">{c.q}</blockquote>
          <figcaption className="mt-4 font-hand text-pencil text-base">— {c.role}</figcaption>
        </figure>
      ))}
    </div>
  </div>
);

export default function CustomPrograms() {
  const { t } = useLang();
  const situations = t("cus.situations_items") || [];
  const building = t("cus.building_items") || [];
  const questions = t("cus.question_items") || [];
  const steps = t("cus.process_steps") || [];
  const contexts = t("cus.case_contexts") || [];
  const ecosystem = t("cus.ecosystem_items") || [];
  const success = t("cus.success_items") || [];

  return (
    <>
      <PageMeta
        titleKey="meta.custom_title"
        descriptionKey="meta.custom_description"
        path={ROUTES.customPrograms}
      />
      <PageHero
        testId="cus-hero"
        eyebrow={t("cus.hero_eyebrow")}
        headline={[t("cus.hero_h1_a"), t("cus.hero_h1_b")]}
        emphasis={t("cus.hero_emphasis")}
        emphasisAccent="underline"
        intro={t("cus.hero_intro")}
        meta={<>{t("cus.hero_meta_line1")}<br />{t("cus.hero_meta_line2")}<br /><span className="text-mustard">{t("cus.hero_meta_line3")}</span></>}
      >
        <PrimaryCTA href={ROUTES.bookCorporate} testId="cus-primary-cta" location="hero" targetAssetId="booking_corporate">{t("cus.cta_book")}</PrimaryCTA>
        <OutlineCTA href={ROUTES.contact} testId="cus-brief-cta" location="hero" targetAssetId="contact_request">{t("cus.final_cta_brief")}</OutlineCTA>
      </PageHero>

      <CusTrust t={t} />

      {/* Belief + situations */}
      <section className="relative z-10 py-24 md:py-32" data-testid="cus-belief">
        <div className="max-w-[1300px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-start">
          <Reveal className="lg:col-span-5">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-6">{t("cus.belief_eyebrow")}</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("cus.belief_h1")}<br /><span className="italic">{t("cus.belief_h2")}</span>
            </h2>
            <div className="mt-8 space-y-4 font-body text-lg text-ink-soft max-w-xl leading-relaxed">
              <p>{t("cus.belief_p1")}</p>
              <p>{t("cus.belief_p2")}</p>
              <p><strong className="text-ink font-normal">{t("cus.belief_p3")}</strong></p>
              <p className="font-serif italic text-xl md:text-2xl text-ink">{t("cus.belief_p4")}</p>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7">
            <div className="relative bg-paper border border-ink/12 p-8 md:p-10 shadow-[4px_8px_28px_rgba(27,27,24,0.06)]">
              <Tape className="absolute -top-4 left-8" rotate={-4} width={110} />
              <p className="font-body text-sm uppercase tracking-[0.2em] text-terracotta mb-6">{t("cus.situations_intro")}</p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6">
                {situations.map((s) => (
                  <li key={s} className="flex items-start gap-3">
                    <Tick className="w-4 h-4 mt-1 shrink-0" />
                    <span className="font-body text-ink leading-relaxed">{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Building blocks */}
      <section className="relative z-10 py-24 md:py-32 bg-paper-deep/50 border-y border-ink/10" data-testid="cus-building">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal className="mb-14 max-w-2xl">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">{t("cus.building_eyebrow")}</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("cus.building_h1")}<br /><span className="italic">{t("cus.building_h2")}</span>
            </h2>
          </Reveal>
          <StaggerGroup className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {building.map((b, i) => (
              <StaggerItem key={b.title}>
                <div className="relative bg-paper border border-ink/12 p-8 h-full shadow-[4px_8px_28px_rgba(27,27,24,0.06)]">
                  <span className="font-hand text-terracotta text-2xl leading-none">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-3 font-serif text-2xl md:text-3xl text-ink leading-tight">{b.title}</h3>
                  <p className="mt-4 font-body text-ink-soft leading-relaxed">{b.body}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
          <p className="mt-10 font-hand text-terracotta text-2xl md:text-3xl">{t("cus.building_note")}</p>
        </div>
      </section>

      {/* Programme starts with a question */}
      <section className="relative z-10 py-24 md:py-32" data-testid="cus-question">
        <div className="max-w-[1300px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-start">
          <Reveal className="lg:col-span-6">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">{t("cus.question_eyebrow")}</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("cus.question_h1")}<br /><span className="italic">{t("cus.question_h2")}</span>
            </h2>
            <p className="mt-8 font-serif italic text-xl md:text-2xl text-ink leading-snug max-w-md">{t("cus.question_note")}</p>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-6">
            <p className="font-body text-sm uppercase tracking-[0.2em] text-terracotta mb-5">{t("cus.question_intro")}</p>
            <ul className="space-y-3">
              {questions.map((q) => (
                <li key={q} className="flex items-start gap-4 border-t border-ink/15 pt-4">
                  <span className="font-hand text-terracotta text-lg leading-none mt-1">→</span>
                  <span className="font-body text-lg text-ink leading-relaxed">{q}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* 5-step co-design process */}
      <section className="relative z-10 py-24 md:py-32 bg-paper-deep/50 border-y border-ink/10" data-testid="cus-process">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-start">
          <Reveal className="lg:col-span-5">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">{t("cus.process_eyebrow")}</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("cus.process_h1")}<br /><span className="italic">{t("cus.process_h2")}</span>
            </h2>
          </Reveal>
          <StaggerGroup className="lg:col-span-7 space-y-6">
            {steps.map((step, i) => (
              <StaggerItem key={step.label}>
                <div className="flex gap-6 items-start border-t border-ink/15 pt-6">
                  <span className="font-hand text-terracotta text-3xl leading-none shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <h3 className="font-serif text-2xl text-ink">{step.label}</h3>
                    <p className="mt-2 font-body text-ink-soft leading-relaxed">{step.body}</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Reality pull-quote block */}
      <section className="relative z-10 py-24 md:py-32" data-testid="cus-reality">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10">
          <Reveal>
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-6">{t("cus.reality_eyebrow")}</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("cus.reality_h1")}<br /><span className="italic">{t("cus.reality_h2")}</span>
            </h2>
            <div className="mt-8 space-y-5 font-body text-lg text-ink-soft max-w-2xl leading-relaxed">
              <p>{t("cus.reality_p1")}</p>
              <p>{t("cus.reality_p2")}</p>
              <p className="font-serif italic text-xl text-ink">{t("cus.reality_p3")}</p>
              <p>{t("cus.reality_p4")}</p>
              <p>{t("cus.reality_p5")}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Case Study — Avocado / Recruitment team */}
      <section className="relative z-10 py-24 md:py-32 bg-paper-deep/40 border-y border-ink/10" data-testid="cus-case">
        <div className="max-w-[1300px] mx-auto px-5 md:px-10">
          <Reveal className="mb-12 max-w-3xl">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">{t("cus.case_eyebrow")}</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("cus.case_h1")}<br /><span className="italic">{t("cus.case_h2")}</span>
            </h2>
            <p className="mt-4 font-hand text-terracotta text-lg md:text-xl">{t("cus.case_partner")}</p>
          </Reveal>

          <article className="grid lg:grid-cols-12 gap-8 lg:gap-14">
            <div className="lg:col-span-5">
              <img
                src="/logos/avocado.png"
                alt="Avocado Learning Experiences"
                className="w-[180px] md:w-[220px] h-auto mb-6 opacity-95"
                draggable={false}
              />
              <p className="font-body text-ink-soft leading-relaxed">
                {t("cus.case_intro")}
              </p>

              <div className="mt-8 relative bg-paper border border-ink/12 p-7 shadow-[4px_8px_24px_rgba(27,27,24,0.06)]">
                <Tape className="absolute -top-4 left-6" rotate={-4} width={90} />
                <p className="font-body text-xs uppercase tracking-[0.22em] text-terracotta mb-3">
                  {t("cus.case_elements_label")}
                </p>
                <p className="font-serif text-2xl md:text-3xl italic text-ink leading-snug">
                  {t("cus.case_elements")}
                </p>
              </div>
            </div>

            <div className="lg:col-span-7">
              <p className="font-body text-xs uppercase tracking-[0.22em] text-terracotta mb-3">
                {t("cus.case_contexts_label")}
              </p>
              <ul className="space-y-2 mb-8">
                {contexts.map((c) => (
                  <li key={c} className="flex items-start gap-3">
                    <Tick className="w-4 h-4 mt-1 shrink-0" />
                    <span className="font-body text-ink leading-relaxed">{c}</span>
                  </li>
                ))}
              </ul>

              <p className="font-body text-ink-soft leading-relaxed mb-8">
                {t("cus.case_method")}
              </p>

              <div className="bg-paper border border-ink/12 p-6 mb-6">
                <p className="font-body text-xs uppercase tracking-[0.22em] text-terracotta mb-3">
                  {t("cus.case_valued_label")}
                </p>
                <p className="font-body text-ink leading-relaxed">
                  {t("cus.case_valued")}
                </p>
              </div>

              <blockquote className="mt-8 border-l-2 border-terracotta pl-5 font-serif italic text-2xl md:text-3xl text-ink leading-snug">
                &ldquo;{t("cus.case_takeaway")}&rdquo;
              </blockquote>
            </div>
          </article>
        </div>
      </section>

      {/* Mentimeter ratings + rolling participant quote cards */}
      <section className="relative z-10 py-24 md:py-32" data-testid="cus-workshop">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 mb-14">
          <Reveal className="max-w-3xl">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">{t("cus.workshop_eyebrow")}</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("cus.workshop_h1")}<br /><span className="italic">{t("cus.workshop_h2")}</span>
            </h2>
          </Reveal>
        </div>
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 mb-14">
          <Reveal>
            <div className="relative bg-ink text-paper px-6 md:px-12 py-12 md:py-14">
              <p className="font-body text-xs uppercase tracking-[0.24em] text-mustard mb-8 text-center">
                {t("cus.workshop_ratings_label")}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-6">
                {CASE_STATS.map((s) => (
                  <div key={s.label} className="text-center">
                    <p className="font-serif text-5xl md:text-6xl lg:text-7xl text-mustard leading-none">{s.value}</p>
                    <p className="mt-3 font-body text-xs md:text-sm uppercase tracking-[0.2em] text-paper/80">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
        {/* Rolling side quotes */}
        <Reveal>
          <RollingQuoteCards />
        </Reveal>
      </section>

      {/* Ecosystem */}
      <section className="relative z-10 py-24 md:py-32 bg-paper-deep/50 border-y border-ink/10" data-testid="cus-ecosystem">
        <div className="max-w-[1300px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-start">
          <Reveal className="lg:col-span-5">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">{t("cus.ecosystem_eyebrow")}</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("cus.ecosystem_h1")}<br /><span className="italic">{t("cus.ecosystem_h2")}</span>
            </h2>
            <div className="mt-8">
              <StickyNote rotate={-2} color="#F2EBDA">{t("cus.ecosystem_note")}</StickyNote>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7">
            <ul className="space-y-3">
              {ecosystem.map((e) => (
                <li key={e} className="flex items-start gap-4 border-t border-ink/15 pt-4">
                  <Star className="w-4 h-4 mt-1 shrink-0" />
                  <span className="font-body text-lg text-ink leading-relaxed">{e}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* Success measures */}
      <section className="relative z-10 py-24 md:py-32" data-testid="cus-success">
        <div className="max-w-[1300px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-start">
          <Reveal className="lg:col-span-5">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">{t("cus.success_eyebrow")}</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("cus.success_h1")}<br /><span className="italic">{t("cus.success_h2")}</span>
            </h2>
            <p className="mt-8 font-hand text-terracotta text-2xl leading-snug max-w-md">{t("cus.success_note")}</p>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7">
            <p className="font-body text-sm uppercase tracking-[0.2em] text-terracotta mb-5">{t("cus.success_intro")}</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6 bg-paper border border-ink/12 p-8 shadow-[4px_8px_28px_rgba(27,27,24,0.06)]">
              {success.map((s) => (
                <li key={s} className="flex items-start gap-3">
                  <Tick className="w-4 h-4 mt-1 shrink-0" />
                  <span className="font-body text-ink leading-relaxed">{s}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <ShareCaseCard programmeTitle="Bespoke Corporate Communication Programmes" testId="cus-share" />
      <MethodologyTeaser testId="cus-methodology-teaser" />

      <section className="relative z-10 py-24 md:py-32 bg-ink text-paper" data-testid="cus-final-cta">
        <div className="max-w-[1000px] mx-auto px-5 md:px-10 text-center">
          <Reveal>
            <p className="font-body text-xs uppercase tracking-[0.24em] text-mustard mb-6">{t("cus.final_eyebrow")}</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-paper">
              {t("cus.final_h1")}<br /><span className="italic">{t("cus.final_h2")}</span>
            </h2>
            <p className="mt-6 font-body text-lg text-paper/90 max-w-2xl mx-auto leading-relaxed">{t("cus.final_body")}</p>
          </Reveal>
          <Reveal delay={0.2} className="mt-10 flex justify-center gap-5 flex-wrap">
            <PrimaryCTA href={ROUTES.bookCorporate} testId="cus-final-book-cta" location="final_cta" targetAssetId="booking_corporate">{t("cus.final_cta_book")}</PrimaryCTA>
            <OutlineCTA href={ROUTES.contact} testId="cus-final-brief-cta" location="final_cta" targetAssetId="contact_request"
              className="!border-paper/70 !text-paper hover:!border-mustard hover:!text-mustard">
              {t("cus.final_cta_brief")}
            </OutlineCTA>
          </Reveal>
        </div>
      </section>
    </>
  );
}
